import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { type Components } from 'react-markdown';
import Markdown from 'react-markdown';
import {
  ACCOUNT_ORDER_HISTORY_ROUTE,
  ACCOUNT_PREFERENCES_ROUTE,
  ACCOUNT_SETTINGS_ROUTE,
  EDIT_DELIVERY_GROCERIES_ROUTE,
  EDIT_DELIVERY_RECIPES_ROUTE,
  MY_HUNGRYROOT_ROUTE,
  ORDERS_ROUTE,
  PLAN_ROUTE,
  WALLET_ROUTE,
} from '@hungryroot/routes';

export const defaultNavigateRouteMap: Record<string, string> = {
  food_profile: ACCOUNT_PREFERENCES_ROUTE,
  order_history: ACCOUNT_ORDER_HISTORY_ROUTE,
  account_settings: ACCOUNT_SETTINGS_ROUTE,
};

export const defaultDeepLinkRouteMap: Record<string, string> = {
  myhungryroot: MY_HUNGRYROOT_ROUTE,
  'edit-delivery/recipes': EDIT_DELIVERY_RECIPES_ROUTE,
  'edit-delivery/groceries': EDIT_DELIVERY_GROCERIES_ROUTE,
  plan: PLAN_ROUTE,
  orders: ORDERS_ROUTE,
  wallet: WALLET_ROUTE,
  'account/settings': ACCOUNT_SETTINGS_ROUTE,
  'account/history': ACCOUNT_ORDER_HISTORY_ROUTE,
};

/**
 * Replaces `navigate:key` URLs in markdown link syntax with actual app routes.
 * e.g. `[update your food profile](navigate:food_profile)` -> `[update your food profile](/preferences/)`
 */
export function transformNavigateLinks(
  content: string,
  navigateRouteMap: Record<string, string> = defaultNavigateRouteMap
): string {
  return content.replace(/\(navigate:(\w+)\)/g, (match, key: string) => {
    const route = Object.hasOwn(navigateRouteMap, key)
      ? navigateRouteMap[key]
      : undefined;
    return route ? `(${route})` : match;
  });
}

/**
 * Replaces `hungryroot://path` URLs in markdown link syntax with actual app routes.
 * e.g. `[go to plan](hungryroot://plan)` -> `[go to plan](/plan/)`
 */
export function transformDeepLinks(
  content: string,
  deepLinkRouteMap: Record<string, string> = defaultDeepLinkRouteMap
): string {
  return content.replace(
    /\(hungryroot:\/\/([^)\s]+)\)/g,
    (match, path: string) => {
      const normalizedPath = path.replace(/\/$/, '');
      const route = Object.hasOwn(deepLinkRouteMap, normalizedPath)
        ? deepLinkRouteMap[normalizedPath]
        : undefined;
      return route ? `(${route})` : match;
    }
  );
}

export const chatMarkdownComponents: Components = {
  a: ({ href, children, ...props }) => (
    <a href={href} {...props}>
      {children}
    </a>
  ),
};

type AgentMarkdownContentProps = Omit<
  ComponentPropsWithoutRef<'div'>,
  'children'
> & {
  content: string;
};

export const AgentMarkdownContent = forwardRef<
  HTMLDivElement,
  AgentMarkdownContentProps
>(({ content, ...props }, ref) => (
  <div {...props} ref={ref}>
    <Markdown components={chatMarkdownComponents}>{content}</Markdown>
  </div>
));

AgentMarkdownContent.displayName = 'AgentMarkdownContent';
